import React from 'react'
import { IonCol, IonContent, IonGrid, IonLabel, IonPage, IonRow, IonSpinner, IonText } from '@ionic/react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import HeaderNav from '../../../../components/headerNav/HeaderNav.component'
import { goBack } from '../../../../utilities/navigation.util'
import { formatShortMonthDateTime } from '../../../../utilities/dateTime.util'
import { IQaAssignmentHistoryEntry } from '../store/qaAddendum.types'
import QaFlagHeader from '../../components/QaFlagHeader.component'
import { useQAFlagNavigation } from '../../hooks/useQAFlagNavigation.hook'
import { canViewFlagSelector, getUserIdSelector } from '../../../../features/user/selectors'
import { isFlagViewCreatorBlackedOut, isFlagViewAssignedToBlackedOut, LockType } from '../../utils/qaFlagBlackout.util'
import './AssignmentHistory.page.css'

interface IRenderHistoryEntryProps {
  historyEntry: IQaAssignmentHistoryEntry
  index: number
  total: number
  t: (key: string) => string
  lockType: number
  canViewFlag: boolean
  currentUserId: number
}

const RenderHistoryEntry: React.FC<IRenderHistoryEntryProps> = (props: IRenderHistoryEntryProps) => {
  const { historyEntry, index, total, t, lockType, canViewFlag, currentUserId } = props
  const assignedByDisplay = isFlagViewCreatorBlackedOut(lockType, canViewFlag)
    ? t('qaFlags.qaAddendum.flagResolution.blackout.username')
    : historyEntry?.assignedBy?.name

  const assignedToDisplay = isFlagViewAssignedToBlackedOut(
    lockType,
    Number(historyEntry.assignedTo?.id),
    currentUserId,
    canViewFlag
  )
    ? t('qaFlags.qaAddendum.flagResolution.blackout.allCrew')
    : historyEntry.assignedTo?.name

  return (
    <React.Fragment key={index}>
      <section className='qa-addendum-history-section'>
        <IonGrid className='ion-no-padding'>
          <IonRow className='qa-addendum-reassignment-history-row qa-addendum-detail-row-top'>
            <IonCol size='5'>
              <IonLabel className='qa-addendum-detail-row-key fw-700 font-size-16'>
                {t('qaFlags.qaAddendum.assignmentHistory.assignedBy')}:
              </IonLabel>
            </IonCol>
            <IonCol size='7'>
              <div className='qa-addendum-value-stack'>
                <IonLabel className='qa-addendum-detail-row-value fw-500 font-size-16'>{assignedByDisplay}</IonLabel>
                <IonText className='qa-addendum-value-meta fw-400 font-size-15'>
                  {formatShortMonthDateTime(historyEntry?.assignedDt)}
                </IonText>
              </div>
            </IonCol>
          </IonRow>
          <IonRow className='qa-addendum-reassignment-history-row qa-addendum-detail-row-top'>
            <IonCol size='5'>
              <IonLabel className='qa-addendum-detail-row-key fw-700 font-size-16'>
                {t('qaFlags.qaAddendum.assignmentHistory.assignedTo')}:
              </IonLabel>
            </IonCol>
            <IonCol size='7'>
              <IonLabel className='qa-addendum-detail-row-value fw-500 font-size-16'>{assignedToDisplay}</IonLabel>
            </IonCol>
          </IonRow>
          <IonRow className='qa-addendum-reassignment-history-row qa-addendum-detail-row-top'>
            <IonCol size='5'>
              <IonLabel className='qa-addendum-detail-row-key fw-700 font-size-16'>
                {t('qaFlags.qaAddendum.assignmentHistory.reason')}:
              </IonLabel>
            </IonCol>
            <IonCol size='7'>
              <IonLabel className='qa-addendum-detail-row-value qa-addendum-value-multiline fw-400 font-size-16'>
                {historyEntry?.reason}
              </IonLabel>
            </IonCol>
          </IonRow>
        </IonGrid>
      </section>
      {index < total - 1 && <hr className='field-divider' />}
    </React.Fragment>
  )
}

const AssignmentHistoryPage: React.FC = () => {
  const { t } = useTranslation()
  const { flagDetail, isDetailLoading } = useQAFlagNavigation()
  const canViewFlag = useSelector(canViewFlagSelector)
  const currentUserId = Number(useSelector(getUserIdSelector))
  const lockType = flagDetail?.lockType ?? LockType.Normal
  const reassignmentHistory = flagDetail?.assignmentHistory ?? []

  return (
    <IonPage id='QaAddendumAssignmentHistoryPage'>
      <QaFlagHeader showMenuButton={false} />
      <HeaderNav
        prevLabel={t('common.buttons.Back')}
        isBackButton
        title={t('qaFlags.qaAddendum.assignmentHistory.title')}
        prevOnClick={goBack}
      />
      <IonContent fullscreen color='light' className='qa-addendum-reassignment-history-content'>
        {isDetailLoading || !flagDetail ? (
          <IonSpinner className='procedure-loader' data-testid='QaFlagDetailSpinner' />
        ) : reassignmentHistory.length === 0 ? (
          <IonText className='qa-addendum-reassignment-history-empty ion-padding'>
            {t('qaFlags.qaAddendum.assignmentHistory.emptyState')}
          </IonText>
        ) : (
          <div className='qa-addendum-surface'>
            {reassignmentHistory.map((entry, index) => (
              <RenderHistoryEntry
                key={index}
                historyEntry={entry}
                index={index}
                total={reassignmentHistory.length}
                t={t}
                lockType={lockType}
                canViewFlag={canViewFlag}
                currentUserId={currentUserId}
              />
            ))}
          </div>
        )}
      </IonContent>
    </IonPage>
  )
}

export default AssignmentHistoryPage
